// =============================================================================
// PDF Export Utility - ACDE Frontend
// Generates professional PDF documents for SIPOC and Process data
// =============================================================================

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { SIPOCData, ProcessGraph } from '@acde/shared';

// Datacom brand colors (used in RGB format in jsPDF)
// DATACOM_BLUE: #002BFE -> RGB(0, 43, 254)
// DATACOM_DARK: #00167F -> RGB(0, 22, 127)

interface ExportOptions {
  processName?: string;
  sessionId?: string;
  includeTimestamp?: boolean;
}

/**
 * Export SIPOC data as a professionally formatted PDF
 */
export function exportSIPOCToPDF(
  data: SIPOCData,
  options: ExportOptions = {}
): void {
  const { processName = 'Untitled Process', sessionId, includeTimestamp = true } = options;
  
  // Create PDF in landscape for better table display
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;

  // Add header with Datacom branding
  doc.setFillColor(0, 22, 127); // Datacom dark blue
  doc.rect(0, 0, pageWidth, 25, 'F');

  // Title
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('SIPOC Analysis', margin, 16);

  // Process name subtitle
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(processName, margin, 22);

  // Datacom logo text (right side)
  doc.setFontSize(10);
  doc.text('DATACOM', pageWidth - margin, 16, { align: 'right' });
  doc.setFontSize(8);
  doc.text('Discovery Engine', pageWidth - margin, 21, { align: 'right' });

  // Reset text color
  doc.setTextColor(0, 0, 0);

  // Process summary if available
  let currentY = 35;
  if (data.processSummary) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 100, 100);
    const summaryLines = doc.splitTextToSize(data.processSummary, pageWidth - margin * 2);
    doc.text(summaryLines, margin, currentY);
    currentY += summaryLines.length * 5 + 5;
    doc.setTextColor(0, 0, 0);
  }

  // Prepare SIPOC table data
  const maxRows = Math.max(
    data.suppliers.length,
    data.inputs.length,
    data.processSteps.length,
    data.outputs.length,
    data.customers.length,
    1
  );

  const tableData: string[][] = [];
  for (let i = 0; i < maxRows; i++) {
    const row = [
      data.suppliers[i] 
        ? `${data.suppliers[i].entity}${data.suppliers[i].type === 'external' ? ' (ext)' : ''}`
        : '',
      data.inputs[i] 
        ? `${data.inputs[i].item}${data.inputs[i].source ? ` (from: ${data.inputs[i].source})` : ''}`
        : '',
      data.processSteps[i] 
        ? `${i + 1}. ${data.processSteps[i]}`
        : '',
      data.outputs[i] 
        ? `${data.outputs[i].item}${data.outputs[i].destination ? ` (to: ${data.outputs[i].destination})` : ''}`
        : '',
      data.customers[i] 
        ? `${data.customers[i].entity}${data.customers[i].type === 'external' ? ' (ext)' : ''}`
        : '',
    ];
    tableData.push(row);
  }

  // Generate SIPOC table
  autoTable(doc, {
    startY: currentY,
    head: [[
      { content: 'SUPPLIERS', styles: { fillColor: [0, 43, 254], textColor: 255 } },
      { content: 'INPUTS', styles: { fillColor: [0, 43, 254], textColor: 255 } },
      { content: 'PROCESS', styles: { fillColor: [0, 43, 254], textColor: 255 } },
      { content: 'OUTPUTS', styles: { fillColor: [0, 43, 254], textColor: 255 } },
      { content: 'CUSTOMERS', styles: { fillColor: [0, 43, 254], textColor: 255 } },
    ]],
    body: tableData.length > 0 ? tableData : [['No data', 'No data', 'No data', 'No data', 'No data']],
    theme: 'grid',
    headStyles: {
      fontSize: 10,
      fontStyle: 'bold',
      halign: 'center',
      valign: 'middle',
      cellPadding: 4,
    },
    bodyStyles: {
      fontSize: 9,
      cellPadding: 3,
      valign: 'top',
    },
    columnStyles: {
      0: { cellWidth: (pageWidth - margin * 2) / 5 },
      1: { cellWidth: (pageWidth - margin * 2) / 5 },
      2: { cellWidth: (pageWidth - margin * 2) / 5 },
      3: { cellWidth: (pageWidth - margin * 2) / 5 },
      4: { cellWidth: (pageWidth - margin * 2) / 5 },
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    margin: { left: margin, right: margin },
  });

  // Get the final Y position after the table
  const finalY = (doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY || currentY + 50;

  // Add statistics section
  const statsY = finalY + 10;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Summary Statistics', margin, statsY);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const stats = [
    `Suppliers: ${data.suppliers.length} (${data.suppliers.filter(s => s.type === 'internal').length} internal, ${data.suppliers.filter(s => s.type === 'external').length} external)`,
    `Inputs: ${data.inputs.length}`,
    `Process Steps: ${data.processSteps.length}`,
    `Outputs: ${data.outputs.length}`,
    `Customers: ${data.customers.length} (${data.customers.filter(c => c.type === 'internal').length} internal, ${data.customers.filter(c => c.type === 'external').length} external)`,
  ];
  stats.forEach((stat, i) => {
    doc.text(`• ${stat}`, margin + 5, statsY + 6 + i * 5);
  });

  // Footer
  doc.setFillColor(245, 245, 245);
  doc.rect(0, pageHeight - 12, pageWidth, 12, 'F');
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  
  if (includeTimestamp) {
    doc.text(`Generated: ${new Date().toLocaleString()}`, margin, pageHeight - 5);
  }
  if (sessionId) {
    doc.text(`Session: ${sessionId.substring(0, 8)}...`, pageWidth / 2, pageHeight - 5, { align: 'center' });
  }
  doc.text('© Datacom Systems Limited', pageWidth - margin, pageHeight - 5, { align: 'right' });

  // Download the PDF
  const filename = `SIPOC-${processName.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
}

/**
 * Export Process Map summary as PDF
 */
export function exportProcessMapToPDF(
  graph: ProcessGraph,
  options: ExportOptions = {}
): void {
  const { processName = 'Untitled Process', sessionId, includeTimestamp = true } = options;
  
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;

  // Add header
  doc.setFillColor(0, 22, 127);
  doc.rect(0, 0, pageWidth, 25, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Process Map Summary', margin, 16);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(processName, margin, 22);

  doc.setFontSize(10);
  doc.text('DATACOM', pageWidth - margin, 16, { align: 'right' });
  doc.setFontSize(8);
  doc.text('Discovery Engine', pageWidth - margin, 21, { align: 'right' });

  doc.setTextColor(0, 0, 0);

  // Process steps table
  const tableData = graph.nodes.map((node, index) => [
    (index + 1).toString(),
    node.type.charAt(0).toUpperCase() + node.type.slice(1),
    node.label,
    node.description || '-',
    node.role || '-',
  ]);

  autoTable(doc, {
    startY: 35,
    head: [[
      { content: '#', styles: { fillColor: [0, 43, 254], textColor: 255 } },
      { content: 'Type', styles: { fillColor: [0, 43, 254], textColor: 255 } },
      { content: 'Step', styles: { fillColor: [0, 43, 254], textColor: 255 } },
      { content: 'Description', styles: { fillColor: [0, 43, 254], textColor: 255 } },
      { content: 'Role', styles: { fillColor: [0, 43, 254], textColor: 255 } },
    ]],
    body: tableData.length > 0 ? tableData : [['1', '-', 'No steps defined', '-', '-']],
    theme: 'grid',
    headStyles: {
      fontSize: 10,
      fontStyle: 'bold',
      halign: 'center',
      valign: 'middle',
      cellPadding: 4,
    },
    bodyStyles: {
      fontSize: 9,
      cellPadding: 3,
      valign: 'top',
    },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 25 },
      2: { cellWidth: 45 },
      3: { cellWidth: 60 },
      4: { cellWidth: 35 },
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    margin: { left: margin, right: margin },
  });

  // Footer
  doc.setFillColor(245, 245, 245);
  doc.rect(0, pageHeight - 12, pageWidth, 12, 'F');
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  
  if (includeTimestamp) {
    doc.text(`Generated: ${new Date().toLocaleString()}`, margin, pageHeight - 5);
  }
  if (sessionId) {
    doc.text(`Session: ${sessionId.substring(0, 8)}...`, pageWidth / 2, pageHeight - 5, { align: 'center' });
  }
  doc.text('© Datacom Systems Limited', pageWidth - margin, pageHeight - 5, { align: 'right' });

  const filename = `ProcessMap-${processName.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
}
